import React, { useMemo } from 'react';

export interface BarChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

export interface OverviewBarChartProps {
  data: BarChartDataPoint[];
  title: string;
  height?: number;
  showValues?: boolean;
  horizontal?: boolean;
  className?: string;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
];

export const OverviewBarChart: React.FC<OverviewBarChartProps> = ({
  data,
  title,
  height = 300,
  showValues = true,
  horizontal = false,
  className = '',
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { bars: [], maxValue: 1 };
    
    const values = data.map(d => d.value);
    const maxValue = Math.max(...values, 1);
    
    const padding = 40;
    const chartWidth = 800;
    const chartHeight = height;
    const graphWidth = chartWidth - padding * 2;
    const graphHeight = chartHeight - padding * 2;
    const barSpacing = horizontal ? 12 : 8;
    const barCount = data.length;
    const availableHeight = graphHeight - (barCount - 1) * barSpacing;
    const barHeight = horizontal ? availableHeight / barCount : 30;
    const barWidth = horizontal ? graphWidth : (graphWidth - (barCount - 1) * barSpacing) / barCount;
    
    const bars = data.map((item, index) => {
      const percentage = (item.value / maxValue) * 100;
      const color = item.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
      
      if (horizontal) {
        const y = padding + index * (barHeight + barSpacing);
        const width = (item.value / maxValue) * graphWidth;
        return {
          ...item,
          x: padding,
          y,
          width,
          height: barHeight,
          percentage,
          color,
          valueX: padding + width + 10,
          valueY: y + barHeight / 2,
        };
      } else {
        const x = padding + index * (barWidth + barSpacing);
        const barValueHeight = (item.value / maxValue) * graphHeight;
        const y = padding + graphHeight - barValueHeight;
        return {
          ...item,
          x,
          y,
          width: barWidth,
          height: barValueHeight,
          percentage,
          color,
          valueX: x + barWidth / 2,
          valueY: y - 5,
        };
      }
    });
    
    return { bars, maxValue, chartWidth, chartHeight, padding, graphWidth, graphHeight };
  }, [data, height, horizontal]);

  if (!data || data.length === 0) {
    return (
      <div className={`card p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className={`card p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${chartData.chartWidth} ${chartData.chartHeight}`}
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid Lines */}
          <g className="text-gray-300 dark:text-gray-700">
            {[0, 1, 2, 3, 4].map((i) => {
              const value = (i / 4) * chartData.maxValue;
              const y = horizontal
                ? chartData.padding + (i / 4) * chartData.graphHeight
                : chartData.padding + chartData.graphHeight - (i / 4) * chartData.graphHeight;
              return (
                <g key={i}>
                  <line
                    x1={chartData.padding}
                    y1={y}
                    x2={chartData.chartWidth - chartData.padding}
                    y2={y}
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  {!horizontal && (
                    <text
                      x={chartData.padding - 10}
                      y={y + 4}
                      textAnchor="end"
                      className="text-xs fill-gray-500 dark:fill-gray-400"
                    >
                      {Math.round(value).toLocaleString()}
                    </text>
                  )}
                </g>
              );
            })}
          </g>

          {/* Bars */}
          {chartData.bars.map((bar, index) => (
            <g key={index}>
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill={bar.color}
                className="hover:opacity-80 transition-opacity"
                rx="4"
              >
                <title>
                  {bar.label}: {bar.value.toLocaleString()} ({bar.percentage.toFixed(1)}%)
                </title>
              </rect>
              
              {/* Value Labels */}
              {showValues && bar.value > 0 && (
                <text
                  x={horizontal ? bar.valueX : bar.valueX}
                  y={horizontal ? bar.valueY : bar.valueY}
                  textAnchor={horizontal ? 'start' : 'middle'}
                  className="text-xs font-medium fill-gray-700 dark:fill-gray-300"
                  alignmentBaseline="middle"
                >
                  {bar.value.toLocaleString()}
                </text>
              )}
              
              {/* Label */}
              {horizontal && (
                <text
                  x={chartData.padding - 10}
                  y={bar.y + bar.height / 2}
                  textAnchor="end"
                  className="text-xs fill-gray-700 dark:fill-gray-300"
                  alignmentBaseline="middle"
                >
                  {bar.label}
                </text>
              )}
              {!horizontal && (
                <text
                  x={bar.x + bar.width / 2}
                  y={chartData.chartHeight - chartData.padding + 20}
                  textAnchor="middle"
                  className="text-xs fill-gray-700 dark:fill-gray-300"
                >
                  {bar.label.length > 10 ? bar.label.substring(0, 10) + '...' : bar.label}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};
